import { Send } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { formatDateTime, formatRelativeDate } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

import { useAddComplaintComment } from '../api/complaints.queries';
import type { ComplaintComment, ComplaintDetails } from '../types/complaint.types';

const REPLY_MAX_LENGTH = 2000;

/**
 * The complaint as a conversation: the customer on one side, support on the
 * other, oldest first, with the reply box underneath. The customer's own
 * complaint is the first message (the backend stores it as one), so it is not
 * repeated anywhere else on the page.
 */
export function ComplaintConversation({
  data,
  canReply,
  zone,
}: {
  data: ComplaintDetails;
  canReply: boolean;
  /** The complaint's market zone. */
  zone: string;
}) {
  const complaint = data.complaint;
  // Older complaints may have no comments at all: show what was raised.
  const messages: ComplaintComment[] =
    data.comments.length > 0
      ? data.comments
      : data.details
        ? [
            {
              id: 0,
              text: data.details,
              authorName: complaint.authorName,
              createdAt: complaint.complaintDate,
              isStaff: false,
            },
          ]
        : [];

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="text-sm">Conversation</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-4">
        {messages.length === 0 ? (
          <p className="text-muted-foreground text-xs">Nothing has been written yet.</p>
        ) : (
          <MessageList messages={messages} zone={zone} />
        )}

        {canReply && <ReplyForm complaintId={complaint.id} isOpen={complaint.status === 'open'} />}
      </CardContent>
    </Card>
  );
}

/** Within this many pixels of the bottom counts as "reading the latest". */
const STICK_TO_BOTTOM_PX = 80;

/**
 * The messages in their own scroll area, like a chat: it opens on the newest
 * message and follows new ones — unless you have scrolled up to read older
 * ones, which a new message must not yank you away from.
 */
function MessageList({ messages, zone }: { messages: ComplaintComment[]; zone: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const newest = messages.at(-1);

  useLayoutEffect(() => {
    const area = scrollRef.current;
    // Our own side's new message always scrolls into view, as in a chat app.
    if (area && (atBottom.current || newest?.isStaff)) {
      area.scrollTop = area.scrollHeight;
      atBottom.current = true;
    }
  }, [newest?.id, newest?.isStaff]);

  return (
    <div
      ref={scrollRef}
      onScroll={(event) => {
        const area = event.currentTarget;
        atBottom.current =
          area.scrollHeight - area.scrollTop - area.clientHeight < STICK_TO_BOTTOM_PX;
      }}
      // Scrollable by keyboard too: the region takes focus.
      tabIndex={0}
      role="region"
      aria-label="Messages"
      className="-me-2 max-h-[min(60vh,560px)] overflow-y-auto overscroll-contain pe-2"
    >
      <ol className="flex flex-col gap-4">
        {messages.map((message) => (
          <Message key={message.id} message={message} zone={zone} />
        ))}
      </ol>
    </div>
  );
}

/**
 * The same layout as the customer's own conversation page, seen from our side:
 * support (us) on the end side in a solid brand bubble with white text, the
 * customer on the start side in a light brand bubble with dark text and an
 * initials avatar. Name above, time below.
 */
function Message({ message, zone }: { message: ComplaintComment; zone: string }) {
  const name = message.authorName ?? (message.isStaff ? 'Support' : 'Customer');
  const mine = message.isStaff;
  return (
    <li className={cn('flex max-w-[80%] items-start gap-2', mine && 'flex-row-reverse self-end')}>
      {!mine && (
        <Avatar className="size-8 shrink-0">
          <AvatarFallback className="bg-status-brand-bg text-primary text-[11px] font-bold">
            {initials(name)}
          </AvatarFallback>
        </Avatar>
      )}
      <div className={cn('flex min-w-0 flex-col gap-1', mine ? 'items-end' : 'items-start')}>
        <span className="text-muted-foreground text-xs font-semibold">{name}</span>
        <p
          className={cn(
            'rounded-xl px-4 py-2 text-sm leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap',
            mine ? 'bg-primary text-primary-foreground' : 'bg-status-brand-bg text-foreground',
          )}
        >
          {message.text}
        </p>
        <time
          dateTime={message.createdAt ?? undefined}
          title={formatRelativeDate(message.createdAt)}
          className="text-muted-foreground text-xs"
        >
          {formatDateTime(message.createdAt, zone)}
        </time>
      </div>
    </li>
  );
}

/** Tallest the reply box grows (about five lines) before it scrolls. */
const REPLY_MAX_HEIGHT_PX = 120;

/**
 * A chat input: one line that grows with the text, and a send button beside
 * it. Enter sends; Shift + Enter starts a new line.
 */
function ReplyForm({ complaintId, isOpen }: { complaintId: number; isOpen: boolean }) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const addComment = useAddComplaintComment(complaintId);
  const trimmed = text.trim();

  // Fit the box to its text: back to one line when cleared after sending.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, REPLY_MAX_HEIGHT_PX)}px`;
  }, [text]);

  const send = () => {
    if (!trimmed || addComment.isPending) return;
    addComment.mutate(trimmed, {
      onSuccess: () => {
        setText('');
        inputRef.current?.focus();
      },
    });
  };

  return (
    <form
      className="border-border flex flex-col gap-1.5 border-t pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <Label htmlFor="complaint-reply" className="sr-only">
        Reply to the customer
      </Label>
      {/* The pill carries the focus ring for the whole input, so the textarea
          inside drops its own — otherwise two rings show, one inside the other. */}
      <div className="border-input focus-within:ring-ring/50 focus-within:border-ring flex items-end gap-2 rounded-3xl border py-1.5 ps-4 pe-1.5 focus-within:ring-[3px]">
        <textarea
          ref={inputRef}
          id="complaint-reply"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            // Not while an IME is composing (Arabic and other input methods).
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              send();
            }
          }}
          maxLength={REPLY_MAX_LENGTH}
          rows={1}
          placeholder="Type a message…"
          aria-describedby="complaint-reply-hint"
          className="placeholder:text-muted-foreground max-h-30 min-h-8 flex-1 resize-none border-0 bg-transparent py-1.5 text-sm leading-5 outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <Button
          type="submit"
          size="icon"
          className="size-8 shrink-0 rounded-full"
          disabled={!trimmed || addComment.isPending}
          aria-label="Send reply"
        >
          <Send className="size-4 rtl:-scale-x-100" aria-hidden />
        </Button>
      </div>
      <p id="complaint-reply-hint" className="text-muted-foreground ps-4 text-[11px]">
        {isOpen
          ? 'Enter to send, Shift + Enter for a new line. The customer sees it from Digital Codex Support.'
          : 'This complaint is closed, but the customer still sees your reply. Enter to send.'}
      </p>
    </form>
  );
}

function initials(name: string) {
  return (
    name
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '?'
  );
}
